'use client';

import React, { useId, useImperativeHandle, useRef, useState, type Ref } from 'react';
import dynamic from 'next/dynamic';
import { Loader2, X } from 'lucide-react';
import { getCurrentUserSession } from '@/app/actions/auth';

// The form is fetched when the dialog first opens, not with every page: the
// dialog sits in the footer of all of them, and few people ever open it.
const FeedbackForm = dynamic(() => import('./FeedbackForm'), { ssr: false, loading: () => <Spinner /> });

function Spinner() {
  return (
    <div role="status" className="py-16 flex justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-gold" />
    </div>
  );
}

// The feedback form in a native <dialog>: focus moves into it and back, Escape
// closes it, and the page behind cannot be tabbed into - without a dialog
// library. Used by "Report a problem on this page" and by "Contact us".

export type FeedbackDialogHandle = { open: () => void };

type Account = { name: string; email: string };

export default function FeedbackDialog({
  ref,
  lang,
  kind,
  title,
  showPage = false,
}: {
  ref: Ref<FeedbackDialogHandle>;
  lang: string;
  kind: 'contact' | 'page_error';
  title: string;
  /** Show the address of the page the report is about, under the title. */
  showPage?: boolean;
}) {
  const isFr = lang === 'fr';
  const titleId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  // A fresh form each time the dialog opens, and none before the first open.
  const [openCount, setOpenCount] = useState(0);
  const [pageUrl, setPageUrl] = useState('');
  // Undefined while the session is being read: the form waits for it so that a
  // signed-in person finds their name and email filled in, as on /contact.
  const [account, setAccount] = useState<Account | null | undefined>(undefined);

  useImperativeHandle(ref, () => ({
    open() {
      setPageUrl(window.location.pathname + window.location.search);
      setAccount(undefined);
      setOpenCount((n) => n + 1);
      dialog.current?.showModal();
      getCurrentUserSession()
        .then((s) => setAccount(s ? { name: s.name || '', email: s.email || '' } : null))
        .catch(() => setAccount(null));
    },
  }), []);

  const close = () => dialog.current?.close();

  return (
    <dialog
      ref={dialog}
      aria-labelledby={titleId}
      onClick={(e) => {
        // A click on the backdrop (the dialog element itself) closes it.
        if (e.target === dialog.current) close();
      }}
      // The browser closes a modal dialog on Escape by itself; this makes it
      // certain in browsers and embedded views that do not.
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
      className="m-auto w-[min(34rem,calc(100vw-2rem))] max-h-[calc(100dvh-2rem)] rounded-2xl p-0 bg-neutral-900 text-white border border-neutral-800 shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 id={titleId} className="text-lg font-bold">{title}</h2>
            {showPage && <p className="text-xs text-neutral-400 mt-1 break-all">{pageUrl}</p>}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={isFr ? 'Fermer' : 'Close'}
            className="p-2 -m-2 rounded-lg text-neutral-400 hover:text-white cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {openCount > 0 && account === undefined && (
          <div role="status" aria-label={isFr ? 'Chargement' : 'Loading'} className="py-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gold" />
          </div>
        )}
        {openCount > 0 && account !== undefined && (
          <FeedbackForm
            key={openCount}
            lang={lang}
            kind={kind}
            pageUrl={pageUrl}
            defaultName={account?.name || ''}
            defaultEmail={account?.email || ''}
            onDone={close}
          />
        )}
      </div>
    </dialog>
  );
}
